import React, { useState, useEffect } from 'react';
import {
  Panel,
  PanelType,
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Separator,
  Toggle,
  Label,
  Dropdown,
  IDropdownOption,
  Checkbox,
  Icon,
  ProgressIndicator,
  TagPicker,
  IBasePicker,
  ITag
} from '@fluentui/react';
import {
  GlobeRegular,
  InfoRegular,
  WarningRegular,
  CheckmarkCircleRegular,
  SearchRegular,
  DocumentRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import ScheduleConfigTab from './ScheduleConfigTab';
import styles from './PublicWebConnectorSetup.module.css';

interface PublicWebConnectorSetupProps {
  isVisible: boolean;
  onDismiss: () => void;
  onComplete: (config: any) => void;
}

interface SearchIndex {
  id: string;
  name: string;
  status: string;
}

interface PublicWebDiscoveryResult {
  totalDocuments: number;
  estimatedProcessingTime: number;
  fileTypeBreakdown: { [key: string]: number };
  urlBreakdown: { source_url: string; documents_found: number; }[];
  discoveredUrls: {
    url: string;
    title: string;
    file_type: string;
    size_estimate: number;
    last_modified?: string;
  }[];
  discoveryTime: string;
}

interface FileNameFilter {
  type: 'includes' | 'contains' | 'exact';
  value: string;
  action: 'include' | 'exclude';
}

interface DepthUrlFilter {
  must_contain: string[];
  cannot_contain: string[];
}

interface WebCrawlConfig {
  // Basic settings
  jobName: string;
  description: string;
  
  // Web crawling settings
  startUrls: string[];
  allowedDomains: string[];
  maxDepth: number;
  maxPages: number;
  respectRobotsTxt: boolean;
  crawlDelay: number;
  
  // Content filtering
  includePaths: string[];
  excludePaths: string[];
  contentTypes: string[];
  maxFileSize: number; // in MB
  
  // URL filtering
  urlMustContain: string[];
  urlCannotContain: string[];
  
  // Depth-based URL filtering
  depthUrlFilters: { [depth: number]: DepthUrlFilter };
  
  // File type prioritization
  fileTypePriority: string[];
  
  // Filename filtering
  filenameFilters: FileNameFilter[];
  filenameFilterLogic: 'AND' | 'OR';
  
  // Advanced filtering options
  advancedFiltering: {
    enabled: boolean;
    filenameFilters: {
      contains: string[];
      patterns: string[];
      startsWith: string[];
      exact: string[];
    };
    urlContentFilters: {
      mustContain: string[];
      cannotContain: string[];
    };
    regexPatterns: {
      include: string[];
      exclude: string[];
    };
    depthLimit: number;
    maxFileSize: number; // in MB
  };
  
  // Processing settings
  enableEmbeddings: boolean;
  enableNER: boolean; // Named Entity Recognition
  enableSummarization: boolean;
  
  // Processing mode settings
  processingMode: 'realtime' | 'batch'; // New processing mode option
  storageContainer: string; // Container name for batch processing
  
  // Index settings
  targetIndex: string;
  createNewIndex: boolean;
  newIndexName: string;
  
  // Advanced settings
  userAgent: string;
  followRedirects: boolean;
  enableJavaScript: boolean;
  waitTime: number; // seconds to wait for JS content
  customHeaders: { [key: string]: string };
  
  // Scheduling configuration
  schedule: {
    enabled: boolean;
    frequency: 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
    startTime?: string; // ISO datetime for first run
    timeOfDay?: string; // HH:MM format
    dayOfWeek?: number; // 0-6 for weekly
    dayOfMonth?: number; // 1-31 for monthly
    intervalMinutes?: number; // For hourly
    cronExpression?: string; // For custom
    timezone: string;
    endDate?: string; // Optional end date
    maxRuns?: number; // Maximum executions
    retryOnFailure: boolean;
    retryCount: number;
    retryDelayMinutes: number;
  };
}

const PublicWebConnectorSetup: React.FC<PublicWebConnectorSetupProps> = ({
  isVisible,
  onDismiss,
  onComplete
}) => {
  // State
  const [config, setConfig] = useState<WebCrawlConfig>({
    jobName: '',
    description: '',
    startUrls: [''],
    allowedDomains: [''],
    maxDepth: 2,
    maxPages: 100,
    respectRobotsTxt: true,
    crawlDelay: 1,
    includePaths: [],
    excludePaths: [],
    contentTypes: ['text/html', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    maxFileSize: 10,
    urlMustContain: [],
    urlCannotContain: [],
    depthUrlFilters: {},
    fileTypePriority: [],
    filenameFilters: [],
    filenameFilterLogic: 'OR',
    // Advanced filtering options
    advancedFiltering: {
      enabled: false,
      filenameFilters: {
        contains: [],
        patterns: [],
        startsWith: [],
        exact: []
      },
      urlContentFilters: {
        mustContain: [],
        cannotContain: []
      },
      regexPatterns: {
        include: [],
        exclude: []
      },
      depthLimit: 10,
      maxFileSize: 50
    },
    enableEmbeddings: true,
    enableNER: false,
    enableSummarization: false,
    processingMode: 'realtime',
    storageContainer: '',
    targetIndex: '',
    createNewIndex: false,
    newIndexName: '',
    userAgent: 'BynetAI-WebCrawler/1.0',
    followRedirects: true,
    enableJavaScript: true,
    waitTime: 3,
    customHeaders: {},
    // Scheduling configuration
    schedule: {
      enabled: false,
      frequency: 'once' as 'once' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom',
      timeOfDay: '07:00',
      timezone: 'Asia/Jerusalem',
      retryOnFailure: true,
      retryCount: 3,
      retryDelayMinutes: 15
    }
  });

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
  const [availableIndexes, setAvailableIndexes] = useState<SearchIndex[]>([]);
  const [validationResults, setValidationResults] = useState<{ [url: string]: { valid: boolean; message: string } }>({});
  const [step, setStep] = useState(1);
  const [totalSteps] = useState(4);
  
  // Discovery state
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState<PublicWebDiscoveryResult | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Content type options
  const contentTypeOptions: IDropdownOption[] = [
    { key: 'text/html', text: 'HTML Pages' },
    { key: 'application/pdf', text: 'PDF Documents' },
    { key: 'application/msword', text: 'Word Documents (.doc)' },
    { key: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', text: 'Word Documents (.docx)' },
    { key: 'application/vnd.ms-excel', text: 'Excel Files (.xls)' },
    { key: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', text: 'Excel Files (.xlsx)' },
    { key: 'application/vnd.ms-powerpoint', text: 'PowerPoint Files (.ppt)' },
    { key: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', text: 'PowerPoint Files (.pptx)' },
    { key: 'text/plain', text: 'Text Files' },
    { key: 'application/json', text: 'JSON Files' },
    { key: 'application/xml', text: 'XML Files' },
    { key: 'text/xml', text: 'XML Files (text)' }
  ];

  // Load available indexes on mount
  useEffect(() => {
    if (isVisible) {
      loadAvailableIndexes();
    }
  }, [isVisible]);

  const loadAvailableIndexes = async () => {
    try {
      const response = await authenticatedFetch('/api/connector-hub/indexes');
      const result = await response.json();
      
      if (result.success && Array.isArray(result.indexes)) {
        setAvailableIndexes(result.indexes);
      }
    } catch (error) {
      console.error('Error loading indexes:', error);
    }
  };

  const handleUrlChange = (index: number, value: string, type: 'startUrls' | 'allowedDomains') => {
    const newUrls = [...config[type]];
    newUrls[index] = value;
    setConfig({ ...config, [type]: newUrls });
  };

  const addUrlField = (type: 'startUrls' | 'allowedDomains') => {
    setConfig({ ...config, [type]: [...config[type], ''] });
  };

  const removeUrlField = (index: number, type: 'startUrls' | 'allowedDomains') => {
    const newUrls = config[type].filter((_, i) => i !== index);
    setConfig({ ...config, [type]: newUrls });
  };

  const validateUrls = async () => {
    setValidating(true);
    setValidationResults({});
    
    try {
      const urlsToValidate = config.startUrls.filter(url => url.trim() !== '');
      const results: { [url: string]: { valid: boolean; message: string } } = {};
      
      for (const url of urlsToValidate) {
        try {
          const response = await authenticatedFetch('/api/connector-hub/validate-url', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url })
          });
          
          const result = await response.json();
          results[url] = {
            valid: result.valid,
            message: result.message || (result.valid ? 'URL is accessible' : 'URL validation failed')
          };
        } catch (error) {
          results[url] = {
            valid: false,
            message: 'Failed to validate URL'
          };
        }
      }
      
      setValidationResults(results);
    } catch (error) {
      console.error('Error validating URLs:', error);
      setMessage({
        text: 'Error validating URLs. Please check your connections and try again.',
        type: MessageBarType.error
      });
    } finally {
      setValidating(false);
    }
  };

  const handleDiscovery = async () => {
    const urlsToDiscover = config.startUrls.filter(url => url.trim().length > 0);
    
    if (urlsToDiscover.length === 0) {
      setMessage({
        text: 'Please provide at least one start URL before running discovery',
        type: MessageBarType.warning
      });
      return;
    }

    // Convert content types to file extensions for the discovery call
    const getFileTypesFromContentTypes = (contentTypes: string[]) => {
      const mapping: { [key: string]: string } = {
        'text/html': '.html',
        'application/pdf': '.pdf',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'application/vnd.ms-powerpoint': '.ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
        'text/plain': '.txt',
        'text/csv': '.csv',
        'application/rtf': '.rtf',
        'application/json': '.json',
        'application/xml': '.xml',
        'text/xml': '.xml'
      };
      
      return contentTypes.map(contentType => mapping[contentType] || '.html').filter(Boolean);
    };

    const fileTypesToDiscover = getFileTypesFromContentTypes(config.contentTypes);

    setDiscoveryLoading(true);
    try {
      // Use the user's actual configuration for discovery
      const discoveryRequest = {
        start_urls: urlsToDiscover,
        max_pages: config.maxPages,
        max_depth: config.maxDepth,
        file_types: fileTypesToDiscover, // Use configured file types
        filename_filters: config.filenameFilters, // Include filename filters
        filename_filter_logic: config.filenameFilterLogic, // Include filter logic
        url_must_contain: config.urlMustContain.filter(pattern => pattern.trim() !== ''),
        url_cannot_contain: config.urlCannotContain.filter(pattern => pattern.trim() !== ''),
        depth_url_filters: config.depthUrlFilters, // Depth-based URL filtering
        file_type_priority: config.fileTypePriority, // File type prioritization
        enable_js_rendering: config.enableJavaScript, // Enable JavaScript rendering
        use_playwright: config.enableJavaScript, // Use Playwright for JS rendering
        wait_time: config.waitTime, // Wait time for JS content
        connector_type: 'public_web'
      };

      console.log('Public Web Discovery request:', discoveryRequest);

      const response = await authenticatedFetch('/api/connector-hub/discovery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(discoveryRequest)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Public Web Discovery response:', data);

      if (data.success) {
        const discoveryData = data.discovery_results;
        
        // Transform the response to match our interface
        const result: PublicWebDiscoveryResult = {
          totalDocuments: discoveryData.total_documents_found || 0,
          estimatedProcessingTime: discoveryData.estimated_processing_time || 0,
          fileTypeBreakdown: discoveryData.file_type_breakdown || {},
          urlBreakdown: discoveryData.url_breakdown || [],
          discoveredUrls: discoveryData.discovered_urls || [],
          discoveryTime: new Date().toISOString()
        };
        
        setDiscoveryResult(result);
        setMessage({
          text: `Discovery completed! Found ${result.totalDocuments} documents.`,
          type: MessageBarType.success
        });
      } else {
        throw new Error(data.error || 'Discovery failed');
      }
    } catch (error) {
      console.error('Discovery error:', error);
      setMessage({
        text: `Discovery failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: MessageBarType.error
      });
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!config.jobName.trim()) {
      setMessage({ text: 'Please enter a job name.', type: MessageBarType.error });
      return;
    }

    const validUrls = config.startUrls.filter(url => url.trim() !== '');
    if (validUrls.length === 0) {
      setMessage({ text: 'Please enter at least one start URL.', type: MessageBarType.error });
      return;
    }

    if (config.createNewIndex && !config.newIndexName.trim()) {
      setMessage({ text: 'Please enter a name for the new index.', type: MessageBarType.error });
      return;
    }

    if (!config.createNewIndex && !config.targetIndex) {
      setMessage({ text: 'Please select an existing index.', type: MessageBarType.error });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Prepare the job configuration
      const jobConfig = {
        name: config.jobName,
        description: config.description,
        connectorTemplate: 'public_website',
        targetIndex: config.createNewIndex ? config.newIndexName : config.targetIndex,
        createNewIndex: config.createNewIndex,
        configuration: {
          start_urls: config.startUrls.filter(url => url.trim() !== ''),
          allowed_domains: config.allowedDomains.filter(domain => domain.trim() !== ''),
          max_depth: config.maxDepth,
          max_pages: config.maxPages,
          respect_robots_txt: config.respectRobotsTxt,
          crawl_delay: config.crawlDelay,
          include_paths: config.includePaths,
          exclude_paths: config.excludePaths,
          content_types: config.contentTypes,
          max_file_size_mb: config.maxFileSize,
          url_must_contain: config.urlMustContain.filter(pattern => pattern.trim() !== ''),
          url_cannot_contain: config.urlCannotContain.filter(pattern => pattern.trim() !== ''),
          depth_url_filters: config.depthUrlFilters,
          file_type_priority: config.fileTypePriority,
          filename_filters: config.filenameFilters,
          filename_filter_logic: config.filenameFilterLogic,
          enable_embeddings: config.enableEmbeddings,
          enable_ner: config.enableNER,
          enable_summarization: config.enableSummarization,
          processing_mode: config.processingMode,
          storage_container: config.storageContainer,
          user_agent: config.userAgent,
          follow_redirects: config.followRedirects,
          enable_javascript: config.enableJavaScript,
          wait_time: config.waitTime,
          custom_headers: config.customHeaders,
          ...(config.advancedFiltering.enabled && {
            advanced_filtering: {
              filename_filters: {
                contains_patterns: config.advancedFiltering.filenameFilters.contains.filter(p => p.trim()),
                starts_with_patterns: config.advancedFiltering.filenameFilters.startsWith.filter(p => p.trim()),
                exact_matches: config.advancedFiltering.filenameFilters.exact.filter(p => p.trim()),
                pattern_matches: config.advancedFiltering.filenameFilters.patterns.filter(p => p.trim())
              },
              url_content_filters: {
                must_contain: config.advancedFiltering.urlContentFilters.mustContain.filter(p => p.trim()),
                cannot_contain: config.advancedFiltering.urlContentFilters.cannotContain.filter(p => p.trim())
              },
              regex_patterns: {
                include_patterns: config.advancedFiltering.regexPatterns.include.filter(p => p.trim()),
                exclude_patterns: config.advancedFiltering.regexPatterns.exclude.filter(p => p.trim())
              },
              advanced_options: {
                max_depth_limit: config.advancedFiltering.depthLimit,
                max_file_size_mb: config.advancedFiltering.maxFileSize
              }
            }
          })
        }
      };

      const response = await authenticatedFetch('/api/connector-hub/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(jobConfig)
      });

      const result = await response.json();

      if (result.success) {
        setMessage({
          text: `Public web connector job "${config.jobName}" created successfully!`,
          type: MessageBarType.success
        });
        
        // Call onComplete after a short delay to show the success message
        setTimeout(() => {
          onComplete(jobConfig);
        }, 1500);
      } else {
        setMessage({
          text: result.error || 'Failed to create public web connector job.',
          type: MessageBarType.error
        });
      }
    } catch (error) {
      console.error('Error creating public web connector job:', error);
      setMessage({
        text: 'Error creating public web connector job. Please try again.',
        type: MessageBarType.error
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = () => {
    switch (step) {
      case 1:
        return (
          <Stack tokens={{ childrenGap: 20 }}>
            <Stack tokens={{ childrenGap: 15 }}>
              <Text variant="xLarge" style={{ fontWeight: 600, color: '#323130' }}>
                Basic Configuration
              </Text>
              <Text variant="medium" style={{ color: '#605e5c' }}>
                Configure the basic settings for your web crawling job.
              </Text>
            </Stack>

            <TextField
              label="Job Name"
              placeholder="e.g., Company Website Crawl"
              value={config.jobName}
              onChange={(_, value) => setConfig({ ...config, jobName: value || '' })}
              required
            />

            <TextField
              label="Description"
              placeholder="Brief description of what this job will crawl"
              value={config.description}
              onChange={(_, value) => setConfig({ ...config, description: value || '' })}
              multiline
              rows={3}
            />

            <Stack tokens={{ childrenGap: 10 }}>
              <Label required>Start URLs</Label>
              <Text variant="small" style={{ color: '#605e5c' }}>
                Enter the URLs where crawling should begin. The crawler will start from these pages.
              </Text>
              {config.startUrls.map((url, index) => (
                <Stack key={index} horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                  <TextField
                    placeholder="https://example.com"
                    value={url}
                    onChange={(_, value) => handleUrlChange(index, value || '', 'startUrls')}
                    style={{ flex: 1 }}
                  />
                  {config.startUrls.length > 1 && (
                    <DefaultButton
                      text="Remove"
                      onClick={() => removeUrlField(index, 'startUrls')}
                    />
                  )}
                </Stack>
              ))}
              <DefaultButton
                text="Add URL"
                onClick={() => addUrlField('startUrls')}
                iconProps={{ iconName: 'Add' }}
              />
            </Stack>

            <Stack tokens={{ childrenGap: 10 }}>
              <Label>Allowed Domains (Optional)</Label>
              <Text variant="small" style={{ color: '#605e5c' }}>
                Restrict crawling to specific domains. Leave empty to allow all domains from start URLs.
              </Text>
              {config.allowedDomains.map((domain, index) => (
                <Stack key={index} horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                  <TextField
                    placeholder="example.com"
                    value={domain}
                    onChange={(_, value) => handleUrlChange(index, value || '', 'allowedDomains')}
                    style={{ flex: 1 }}
                  />
                  {config.allowedDomains.length > 1 && (
                    <DefaultButton
                      text="Remove"
                      onClick={() => removeUrlField(index, 'allowedDomains')}
                    />
                  )}
                </Stack>
              ))}
              <DefaultButton
                text="Add Domain"
                onClick={() => addUrlField('allowedDomains')}
                iconProps={{ iconName: 'Add' }}
              />
            </Stack>
          </Stack>
        );

      case 2:
        return (
          <Stack tokens={{ childrenGap: 20 }}>
            <Stack tokens={{ childrenGap: 15 }}>
              <Text variant="xLarge" style={{ fontWeight: 600, color: '#323130' }}>
                Crawling Settings
              </Text>
              <Text variant="medium" style={{ color: '#605e5c' }}>
                Configure how the crawler should behave and what content to collect.
              </Text>
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 20 }}>
              <TextField
                label="Max Crawl Depth"
                type="number"
                value={config.maxDepth.toString()}
                onChange={(_, value) => setConfig({ ...config, maxDepth: parseInt(value || '2') || 2 })}
                description="How many links deep to crawl (0 = start URLs only)"
                style={{ flex: 1 }}
              />
              <TextField
                label="Max Pages"
                type="number"
                value={config.maxPages.toString()}
                onChange={(_, value) => setConfig({ ...config, maxPages: parseInt(value || '100') || 100 })}
                description="Maximum number of pages to crawl"
                style={{ flex: 1 }}
              />
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 20 }}>
              <TextField
                label="Crawl Delay (seconds)"
                type="number"
                value={config.crawlDelay.toString()}
                onChange={(_, value) => setConfig({ ...config, crawlDelay: parseFloat(value || '1') || 1 })}
                description="Delay between requests to be respectful"
                style={{ flex: 1 }}
              />
              <TextField
                label="Max File Size (MB)"
                type="number"
                value={config.maxFileSize.toString()}
                onChange={(_, value) => setConfig({ ...config, maxFileSize: parseInt(value || '10') || 10 })}
                description="Maximum file size to download"
                style={{ flex: 1 }}
              />
            </Stack>

            <Dropdown
              label="Content Types to Crawl"
              multiSelect
              options={contentTypeOptions}
              selectedKeys={config.contentTypes}
              onChange={(_, option) => {
                if (option) {
                  const newContentTypes = option.selected
                    ? [...config.contentTypes, option.key as string]
                    : config.contentTypes.filter(type => type !== option.key);
                  setConfig({ ...config, contentTypes: newContentTypes });
                }
              }}
              placeholder="Select content types to crawl"
            />

            <Separator />

            {/* Smart Filtering Configuration */}
            <Stack tokens={{ childrenGap: 20 }}>
              <Label>🎯 Smart Filtering Configuration</Label>
              <Text variant="small" style={{ color: '#605e5c' }}>
                Configure advanced filtering to focus crawling on relevant content and avoid wasting time on irrelevant pages.
              </Text>
              
              {/* Depth-Based URL Filtering */}
              <Stack tokens={{ childrenGap: 15 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                  <Toggle
                    checked={Object.keys(config.depthUrlFilters).length > 0}
                    onChange={(_, checked) => {
                      if (checked) {
                        setConfig({
                          ...config,
                          depthUrlFilters: {
                            0: { must_contain: [], cannot_contain: [] }
                          }
                        });
                      } else {
                        setConfig({ ...config, depthUrlFilters: {} });
                      }
                    }}
                  />
                  <Text variant="medium" style={{ fontWeight: 600 }}>
                    Depth-Based URL Filtering
                  </Text>
                  <Icon iconName="Info" style={{ cursor: 'pointer' }} title="Configure different URL patterns for each crawling depth level. Depth 0 = start pages, Depth 1 = first level links, etc." />
                </Stack>
                
                {Object.keys(config.depthUrlFilters).length > 0 && (
                  <Stack tokens={{ childrenGap: 15 }} style={{ 
                    padding: '16px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px',
                    border: '1px solid #e1e1e1'
                  }}>
                    <Text variant="small" style={{ color: '#666', marginBottom: '8px' }}>
                      Configure URL patterns for each depth level. Leave empty to apply no restrictions at that depth.
                    </Text>
                    
                    {Array.from({ length: config.maxDepth + 1 }, (_, depth) => (
                      <Stack key={depth} tokens={{ childrenGap: 10 }} style={{
                        padding: '12px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #d1d1d1'
                      }}>
                        <Text variant="medium" style={{ fontWeight: 600 }}>
                          Depth {depth} {depth === 0 ? '(Start Pages)' : depth === 1 ? '(First Level Links)' : '(Level ' + depth + ' Links)'}
                        </Text>
                        
                        <TextField
                          label="URLs must contain (comma-separated patterns)"
                          placeholder={depth === 0 ? "/publications/, /documents/" : "Leave empty for no restrictions"}
                          value={config.depthUrlFilters[depth]?.must_contain?.join(', ') || ''}
                          onChange={(_, value) => {
                            const patterns = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                            setConfig({
                              ...config,
                              depthUrlFilters: {
                                ...config.depthUrlFilters,
                                [depth]: {
                                  ...config.depthUrlFilters[depth],
                                  must_contain: patterns
                                }
                              }
                            });
                          }}
                        />
                        
                        <TextField
                          label="URLs cannot contain (comma-separated patterns)"
                          placeholder="admin, login, private"
                          value={config.depthUrlFilters[depth]?.cannot_contain?.join(', ') || ''}
                          onChange={(_, value) => {
                            const patterns = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                            setConfig({
                              ...config,
                              depthUrlFilters: {
                                ...config.depthUrlFilters,
                                [depth]: {
                                  ...config.depthUrlFilters[depth],
                                  cannot_contain: patterns
                                }
                              }
                            });
                          }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
              
              {/* File Type Priority */}
              <Stack tokens={{ childrenGap: 15 }}>
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                  <Toggle
                    checked={config.fileTypePriority.length > 0}
                    onChange={(_, checked) => {
                      if (checked) {
                        setConfig({ ...config, fileTypePriority: ['.pdf', '.docx', '.html'] });
                      } else {
                        setConfig({ ...config, fileTypePriority: [] });
                      }
                    }}
                  />
                  <Text variant="medium" style={{ fontWeight: 600 }}>
                    File Type Prioritization
                  </Text>
                  <Icon iconName="Info" style={{ cursor: 'pointer' }} title="When multiple file types are found for the same content, prefer the types listed first" />
                </Stack>
                
                {config.fileTypePriority.length > 0 && (
                  <Stack tokens={{ childrenGap: 10 }} style={{ 
                    padding: '16px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '6px',
                    border: '1px solid #e1e1e1'
                  }}>
                    <Text variant="small" style={{ color: '#666', marginBottom: '8px' }}>
                      Drag to reorder priority (highest priority first). When the same document is available in multiple formats, the system will prefer the format listed higher.
                    </Text>
                    
                    {config.fileTypePriority.map((fileType, index) => (
                      <Stack key={index} horizontal tokens={{ childrenGap: 10 }} verticalAlign="center" style={{
                        padding: '8px 12px',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #d1d1d1'
                      }}>
                        <Icon iconName="GripperDotsVertical" style={{ cursor: 'grab', color: '#666' }} />
                        <Text style={{ fontWeight: 600, minWidth: '60px' }}>{index + 1}.</Text>
                        <Dropdown
                          selectedKey={fileType}
                          onChange={(_, option) => {
                            if (option) {
                              const newPriority = [...config.fileTypePriority];
                              newPriority[index] = option.key as string;
                              setConfig({ ...config, fileTypePriority: newPriority });
                            }
                          }}
                          options={[
                            { key: '.pdf', text: 'PDF (.pdf)' },
                            { key: '.docx', text: 'Word 2007+ (.docx)' },
                            { key: '.doc', text: 'Word 97-2003 (.doc)' },
                            { key: '.html', text: 'HTML Pages (.html)' },
                            { key: '.txt', text: 'Text Files (.txt)' },
                            { key: '.xlsx', text: 'Excel 2007+ (.xlsx)' },
                            { key: '.xls', text: 'Excel 97-2003 (.xls)' },
                            { key: '.pptx', text: 'PowerPoint 2007+ (.pptx)' },
                            { key: '.ppt', text: 'PowerPoint 97-2003 (.ppt)' }
                          ]}
                          style={{ flex: 1 }}
                        />
                        <DefaultButton
                          text="Remove"
                          onClick={() => {
                            const newPriority = config.fileTypePriority.filter((_, i) => i !== index);
                            setConfig({ ...config, fileTypePriority: newPriority });
                          }}
                          iconProps={{ iconName: 'Delete' }}
                        />
                      </Stack>
                    ))}
                    
                    <DefaultButton
                      text="Add File Type"
                      onClick={() => {
                        const availableTypes = ['.pdf', '.docx', '.doc', '.html', '.txt', '.xlsx', '.xls', '.pptx', '.ppt'];
                        const nextType = availableTypes.find(type => !config.fileTypePriority.includes(type)) || '.pdf';
                        setConfig({ 
                          ...config, 
                          fileTypePriority: [...config.fileTypePriority, nextType] 
                        });
                      }}
                      iconProps={{ iconName: 'Add' }}
                      style={{ alignSelf: 'flex-start' }}
                    />
                    
                    <Text variant="small" style={{ color: '#0078d4', fontStyle: 'italic' }}>
                      💡 Example: If both a PDF and DOCX version of the same document exist, the system will prefer the type listed first
                    </Text>
                  </Stack>
                )}
              </Stack>
            </Stack>

            <Separator />

            {/* Advanced Section (Collapsible) */}
            <Stack tokens={{ childrenGap: 15 }}>
              <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                <DefaultButton
                  text={showAdvanced ? "Hide Advanced Settings" : "Show Advanced Settings"}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  iconProps={{ iconName: showAdvanced ? 'ChevronUp' : 'ChevronDown' }}
                />
                <Text variant="small" style={{ color: '#666' }}>
                  For power users and custom configurations
                </Text>
              </Stack>
              
              {showAdvanced && (
                <Stack tokens={{ childrenGap: 20 }} style={{ 
                  padding: '16px', 
                  backgroundColor: '#fafafa', 
                  borderRadius: '6px',
                  border: '1px solid #e1e1e1'
                }}>

            <Stack tokens={{ childrenGap: 10 }}>
              <Label>URL Filtering (Optional)</Label>
              <Text variant="small" style={{ color: '#605e5c' }}>
                Filter which URLs to crawl based on patterns. Useful for focusing on specific sections of a website.
              </Text>
              
              <Stack tokens={{ childrenGap: 10 }}>
                <Label>URLs Must Contain (at least one of these patterns)</Label>
                <Text variant="small" style={{ color: '#605e5c' }}>
                  Only crawl URLs that contain at least one of these patterns (e.g., "/publications/", "/docs/")
                </Text>
                {config.urlMustContain.map((pattern, index) => (
                  <Stack key={index} horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                    <TextField
                      placeholder="/publications/"
                      value={pattern}
                      onChange={(_, value) => {
                        const newPatterns = [...config.urlMustContain];
                        newPatterns[index] = value || '';
                        setConfig({ ...config, urlMustContain: newPatterns });
                      }}
                      style={{ flex: 1 }}
                    />
                    <DefaultButton
                      text="Remove"
                      onClick={() => {
                        const newPatterns = config.urlMustContain.filter((_, i) => i !== index);
                        setConfig({ ...config, urlMustContain: newPatterns });
                      }}
                    />
                  </Stack>
                ))}
                <DefaultButton
                  text="Add Pattern"
                  onClick={() => setConfig({ ...config, urlMustContain: [...config.urlMustContain, ''] })}
                  iconProps={{ iconName: 'Add' }}
                />
              </Stack>

              <Stack tokens={{ childrenGap: 10 }}>
                <Label>URLs Cannot Contain (exclude these patterns)</Label>
                <Text variant="small" style={{ color: '#605e5c' }}>
                  Exclude URLs that contain any of these patterns (e.g., "/admin/", "/private/")
                </Text>
                {config.urlCannotContain.map((pattern, index) => (
                  <Stack key={index} horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                    <TextField
                      placeholder="/admin/"
                      value={pattern}
                      onChange={(_, value) => {
                        const newPatterns = [...config.urlCannotContain];
                        newPatterns[index] = value || '';
                        setConfig({ ...config, urlCannotContain: newPatterns });
                      }}
                      style={{ flex: 1 }}
                    />
                    <DefaultButton
                      text="Remove"
                      onClick={() => {
                        const newPatterns = config.urlCannotContain.filter((_, i) => i !== index);
                        setConfig({ ...config, urlCannotContain: newPatterns });
                      }}
                    />
                  </Stack>
                ))}
                <DefaultButton
                  text="Add Pattern"
                  onClick={() => setConfig({ ...config, urlCannotContain: [...config.urlCannotContain, ''] })}
                  iconProps={{ iconName: 'Add' }}
                />
              </Stack>
            </Stack>

                </Stack>
              )}
            </Stack>

            <Stack tokens={{ childrenGap: 15 }}>
              <Label>Crawler Behavior</Label>
              <Toggle
                label="Respect robots.txt"
                checked={config.respectRobotsTxt}
                onChange={(_, checked) => setConfig({ ...config, respectRobotsTxt: checked || false })}
                onText="Enabled"
                offText="Disabled"
              />
              <Toggle
                label="Follow Redirects"
                checked={config.followRedirects}
                onChange={(_, checked) => setConfig({ ...config, followRedirects: checked || false })}
                onText="Enabled"
                offText="Disabled"
              />
              <Toggle
                label="Enable JavaScript Rendering"
                checked={config.enableJavaScript}
                onChange={(_, checked) => setConfig({ ...config, enableJavaScript: checked || false })}
                onText="Enabled"
                offText="Disabled"
              />
              {config.enableJavaScript && (
                <TextField
                  label="Wait Time for JavaScript (seconds)"
                  type="number"
                  value={config.waitTime.toString()}
                  onChange={(_, value) => setConfig({ ...config, waitTime: parseInt(value || '3') || 3 })}
                  description="How long to wait for JavaScript content to load"
                />
              )}
            </Stack>

            <Separator />

            {/* Advanced Filtering Section */}
            <Stack tokens={{ childrenGap: 8 }} styles={{ root: { border: '1px solid #e1e1e1', borderRadius: 4, padding: 16, backgroundColor: '#f9f9f9' } }}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack tokens={{ childrenGap: 2 }}>
                  <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                    <SearchRegular style={{ fontSize: 14, color: '#0078d4' }} />
                    <Text variant="medium" style={{ fontWeight: 600 }}>Advanced Filtering</Text>
                  </Stack>
                  <Text variant="small" style={{ color: '#666' }}>
                    Fine-tune what content to include or exclude during crawling
                  </Text>
                </Stack>
                <Toggle
                  checked={config.advancedFiltering.enabled}
                  onChange={(_, checked) => setConfig(prev => ({ 
                    ...prev, 
                    advancedFiltering: { ...prev.advancedFiltering, enabled: checked || false } 
                  }))}
                  onText="Enabled"
                  offText="Disabled"
                />
              </Stack>

              {config.advancedFiltering.enabled && (
                <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: '12px' }}>
                  {/* Filename Filters */}
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>Filename Filters</Text>
                    <TextField
                      label="Must contain text"
                      placeholder="e.g., report, manual, guide (comma-separated)"
                      value={config.advancedFiltering.filenameFilters.contains.join(', ')}
                      onChange={(_, value) => {
                        const contains = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                        setConfig(prev => ({
                          ...prev,
                          advancedFiltering: {
                            ...prev.advancedFiltering,
                            filenameFilters: { ...prev.advancedFiltering.filenameFilters, contains }
                          }
                        }));
                      }}
                      description="Files must contain at least one of these text patterns in their name"
                    />
                    <TextField
                      label="Wildcard patterns"
                      placeholder="e.g., *manual*, *2024*, guide_* (comma-separated)"
                      value={config.advancedFiltering.filenameFilters.patterns.join(', ')}
                      onChange={(_, value) => {
                        const patterns = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                        setConfig(prev => ({
                          ...prev,
                          advancedFiltering: {
                            ...prev.advancedFiltering,
                            filenameFilters: { ...prev.advancedFiltering.filenameFilters, patterns }
                          }
                        }));
                      }}
                      description="Use * as wildcard. Example: *guide* matches any file containing 'guide'"
                    />
                    <TextField
                      label="Must start with"
                      placeholder="e.g., PUBLIC_, DOCS_, guide_ (comma-separated)"
                      value={config.advancedFiltering.filenameFilters.startsWith.join(', ')}
                      onChange={(_, value) => {
                        const startsWith = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                        setConfig(prev => ({
                          ...prev,
                          advancedFiltering: {
                            ...prev.advancedFiltering,
                            filenameFilters: { ...prev.advancedFiltering.filenameFilters, startsWith }
                          }
                        }));
                      }}
                      description="Files must start with one of these prefixes"
                    />
                  </Stack>

                  {/* URL Content Filters */}
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>URL/Path Filters</Text>
                    <TextField
                      label="URLs must contain"
                      placeholder="e.g., /docs/, /publications/, /resources/ (comma-separated)"
                      value={config.advancedFiltering.urlContentFilters.mustContain.join(', ')}
                      onChange={(_, value) => {
                        const mustContain = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                        setConfig(prev => ({
                          ...prev,
                          advancedFiltering: {
                            ...prev.advancedFiltering,
                            urlContentFilters: { ...prev.advancedFiltering.urlContentFilters, mustContain }
                          }
                        }));
                      }}
                      description="Only crawl URLs containing these terms"
                    />
                    <TextField
                      label="URLs cannot contain"
                      placeholder="e.g., /admin/, /login/, /private/, /wp-admin/ (comma-separated)"
                      value={config.advancedFiltering.urlContentFilters.cannotContain.join(', ')}
                      onChange={(_, value) => {
                        const cannotContain = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                        setConfig(prev => ({
                          ...prev,
                          advancedFiltering: {
                            ...prev.advancedFiltering,
                            urlContentFilters: { ...prev.advancedFiltering.urlContentFilters, cannotContain }
                          }
                        }));
                      }}
                      description="Skip URLs containing these terms"
                    />
                  </Stack>

                  {/* Advanced Options */}
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>Advanced Options</Text>
                    <Stack horizontal tokens={{ childrenGap: 16 }}>
                      <TextField
                        label="Max crawl depth"
                        type="number"
                        value={config.advancedFiltering.depthLimit.toString()}
                        onChange={(_, value) => {
                          const depth = parseInt(value || '10');
                          if (!isNaN(depth) && depth > 0) {
                            setConfig(prev => ({
                              ...prev,
                              advancedFiltering: { ...prev.advancedFiltering, depthLimit: depth }
                            }));
                          }
                        }}
                        styles={{ root: { maxWidth: '120px' } }}
                        description="Max link depth"
                      />
                      <TextField
                        label="Max file size (MB)"
                        type="number"
                        value={config.advancedFiltering.maxFileSize.toString()}
                        onChange={(_, value) => {
                          const size = parseInt(value || '50');
                          if (!isNaN(size) && size > 0) {
                            setConfig(prev => ({
                              ...prev,
                              advancedFiltering: { ...prev.advancedFiltering, maxFileSize: size }
                            }));
                          }
                        }}
                        styles={{ root: { maxWidth: '120px' } }}
                        description="Skip larger files"
                      />
                    </Stack>
                    <TextField
                      label="Exclude regex patterns"
                      placeholder="e.g., \\.css$,\\.js$,.*backup.*,.*\\.tmp$ (comma-separated)"
                      value={config.advancedFiltering.regexPatterns.exclude.join(', ')}
                      onChange={(_, value) => {
                        const exclude = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                        setConfig(prev => ({
                          ...prev,
                          advancedFiltering: {
                            ...prev.advancedFiltering,
                            regexPatterns: { ...prev.advancedFiltering.regexPatterns, exclude }
                          }
                        }));
                      }}
                      description="Advanced regex patterns to exclude files/URLs"
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Stack>
        );

      case 3:
        return (
          <Stack tokens={{ childrenGap: 20 }}>
            <Stack tokens={{ childrenGap: 15 }}>
              <Text variant="xLarge" style={{ fontWeight: 600, color: '#323130' }}>
                Processing & Index Settings
              </Text>
              <Text variant="medium" style={{ color: '#605e5c' }}>
                Configure content processing and where to store the crawled data.
              </Text>
            </Stack>

            <Stack tokens={{ childrenGap: 15 }}>
              <Label>Content Processing</Label>
              <Toggle
                label="Enable Embeddings"
                checked={config.enableEmbeddings}
                onChange={(_, checked) => setConfig({ ...config, enableEmbeddings: checked || false })}
                onText="Enabled"
                offText="Disabled"
              />
              <Toggle
                label="Enable Named Entity Recognition"
                checked={config.enableNER}
                onChange={(_, checked) => setConfig({ ...config, enableNER: checked || false })}
                onText="Enabled"
                offText="Disabled"
              />
              <Toggle
                label="Enable Summarization"
                checked={config.enableSummarization}
                onChange={(_, checked) => setConfig({ ...config, enableSummarization: checked || false })}
                onText="Enabled"
                offText="Disabled"
              />
            </Stack>

            <Separator />

            {/* Processing Mode Selection */}
            <Stack tokens={{ childrenGap: 15 }}>
              <Label>🚀 Processing Mode</Label>
              <Text variant="small" style={{ color: '#605e5c' }}>
                Choose how documents should be processed and indexed. Batch mode is recommended for large volumes (10,000+ documents).
              </Text>
              
              <Dropdown
                label="Processing Mode"
                selectedKey={config.processingMode}
                onChange={(_, option) => {
                  if (option) {
                    setConfig({ 
                      ...config, 
                      processingMode: option.key as 'realtime' | 'batch',
                      // Clear storage container when switching to realtime
                      storageContainer: option.key === 'realtime' ? '' : config.storageContainer
                    });
                  }
                }}
                options={[
                  { 
                    key: 'realtime', 
                    text: '⚡ Real-time Processing',
                    data: { 
                      description: 'Process documents immediately during crawling. Best for smaller datasets (< 1,000 docs).'
                    }
                  },
                  { 
                    key: 'batch', 
                    text: '📦 Batch Processing (Recommended for Large Datasets)',
                    data: { 
                      description: 'Store documents in Azure Storage, then batch process with indexer. Avoids rate limits for large volumes (10,000+ docs).'
                    }
                  }
                ]}
                style={{ width: '100%' }}
              />

              {config.processingMode === 'realtime' && (
                <MessageBar messageBarType={MessageBarType.info}>
                  <Text variant="small">
                    <strong>Real-time Processing:</strong> Documents are processed immediately during crawling. 
                    This mode works well for smaller datasets but may hit Azure AI Search rate limits with large volumes.
                  </Text>
                </MessageBar>
              )}

              {config.processingMode === 'batch' && (
                <Stack tokens={{ childrenGap: 10 }}>
                  <MessageBar messageBarType={MessageBarType.success}>
                    <Text variant="small">
                      <strong>Batch Processing:</strong> Documents are stored in Azure Storage first, then processed using Azure Search indexer. 
                      This approach handles large volumes efficiently and avoids rate limits.
                    </Text>
                  </MessageBar>
                  
                  <TextField
                    label="Azure Storage Container Name"
                    placeholder="e.g., mevaker-documents-2024"
                    value={config.storageContainer}
                    onChange={(_, value) => setConfig({ ...config, storageContainer: value || '' })}
                    onFocus={() => {
                      // Auto-fill container name if empty
                      if (!config.storageContainer.trim() && config.jobName.trim()) {
                        const sanitizedJobName = config.jobName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                        const containerName = `publicweb-${sanitizedJobName}-${Date.now()}`;
                        setConfig({ ...config, storageContainer: containerName });
                      }
                    }}
                    description="Name of the Azure Storage container where documents will be stored before indexing"
                    required
                  />
                  
                  <Text variant="small" style={{ color: '#666', fontStyle: 'italic' }}>
                    💡 The container will be created automatically if it doesn't exist. Use a unique name for each job to avoid conflicts.
                  </Text>
                </Stack>
              )}
            </Stack>

            <Separator />

            <Stack tokens={{ childrenGap: 15 }}>
              <Label>Filename Filtering</Label>
              <Text variant="medium" style={{ color: '#605e5c' }}>
                Filter files by their names using different matching methods.
              </Text>
              
              <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                <Text variant="medium">Apply filters using:</Text>
                <Dropdown
                  options={[
                    { key: 'OR', text: 'OR logic (match any filter)' },
                    { key: 'AND', text: 'AND logic (match all filters)' }
                  ]}
                  selectedKey={config.filenameFilterLogic}
                  onChange={(_, option) => setConfig({ ...config, filenameFilterLogic: option?.key as 'OR' | 'AND' || 'OR' })}
                  style={{ minWidth: 200 }}
                />
              </Stack>

              {config.filenameFilters.map((filter, index) => (
                <Stack key={index} horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                  <Dropdown
                    label={index === 0 ? "Filter Type" : ""}
                    options={[
                      { key: 'includes', text: 'Includes' },
                      { key: 'contains', text: 'Contains' },
                      { key: 'exact', text: 'Exact Match' }
                    ]}
                    selectedKey={filter.type}
                    onChange={(_, option) => {
                      const newFilters = [...config.filenameFilters];
                      newFilters[index] = { ...filter, type: option?.key as 'includes' | 'contains' | 'exact' || 'contains' };
                      setConfig({ ...config, filenameFilters: newFilters });
                    }}
                    style={{ minWidth: 120 }}
                  />
                  
                  <TextField
                    label={index === 0 ? "Filter Value" : ""}
                    placeholder="Enter filename pattern"
                    value={filter.value}
                    onChange={(_, value) => {
                      const newFilters = [...config.filenameFilters];
                      newFilters[index] = { ...filter, value: value || '' };
                      setConfig({ ...config, filenameFilters: newFilters });
                    }}
                    style={{ flex: 1 }}
                  />
                  
                  <Dropdown
                    label={index === 0 ? "Action" : ""}
                    options={[
                      { key: 'include', text: 'Include' },
                      { key: 'exclude', text: 'Exclude' }
                    ]}
                    selectedKey={filter.action}
                    onChange={(_, option) => {
                      const newFilters = [...config.filenameFilters];
                      newFilters[index] = { ...filter, action: option?.key as 'include' | 'exclude' || 'include' };
                      setConfig({ ...config, filenameFilters: newFilters });
                    }}
                    style={{ minWidth: 100 }}
                  />
                  
                  <DefaultButton
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      const newFilters = config.filenameFilters.filter((_, i) => i !== index);
                      setConfig({ ...config, filenameFilters: newFilters });
                    }}
                    title="Remove filter"
                  />
                </Stack>
              ))}

              <DefaultButton
                iconProps={{ iconName: 'Add' }}
                text="Add Filename Filter"
                onClick={() => {
                  const newFilter: FileNameFilter = { type: 'contains', value: '', action: 'include' };
                  setConfig({ ...config, filenameFilters: [...config.filenameFilters, newFilter] });
                }}
              />

              <Text variant="small" style={{ color: '#605e5c' }}>
                <strong>Includes:</strong> Match if filename starts with the pattern<br />
                <strong>Contains:</strong> Match if filename contains the pattern anywhere<br />
                <strong>Exact:</strong> Match if filename exactly equals the pattern
              </Text>
            </Stack>

            <Separator />

            <Stack tokens={{ childrenGap: 15 }}>
              <Label>Index Configuration</Label>
              <Toggle
                label="Create New Index"
                checked={config.createNewIndex}
                onChange={(_, checked) => {
                  if (checked) {
                    // Auto-generate index name with timestamp like SharePoint does
                    const jobName = config.jobName || 'public-web';
                    const sanitizedName = jobName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                    const indexName = `publicweb-${sanitizedName}-${Date.now()}`;
                    setConfig({ 
                      ...config, 
                      createNewIndex: true,
                      newIndexName: indexName
                    });
                  } else {
                    setConfig({ ...config, createNewIndex: false });
                  }
                }}
                onText="Create New"
                offText="Use Existing"
              />

              {config.createNewIndex ? (
                <TextField
                  label="New Index Name"
                  placeholder="e.g., publicweb-company-website-index"
                  value={config.newIndexName}
                  onChange={(_, value) => setConfig({ ...config, newIndexName: value || '' })}
                  required
                  description="Name for the new search index (lowercase, no spaces)"
                />
              ) : (
                <Dropdown
                  label="Select Existing Index"
                  options={availableIndexes.map(index => ({
                    key: index.id,
                    text: `${index.name} (${index.status})`
                  }))}
                  selectedKey={config.targetIndex}
                  onChange={(_, option) => setConfig({ ...config, targetIndex: option?.key as string || '' })}
                  placeholder="Select an index"
                />
              )}
            </Stack>
          </Stack>
        );

      case 4:
        return (
          <ScheduleConfigTab
            schedule={config.schedule as any}
            onChange={(schedule: any) => setConfig({ ...config, schedule: { ...config.schedule, ...schedule } })}
          />
        );

      default:
        return null;
    }
  };

  const getStepProgress = () => {
    return (step - 1) / (totalSteps - 1);
  };

  const canProceedToNextStep = () => {
    switch (step) {
      case 1:
        return config.jobName.trim() !== '' && config.startUrls.some(url => url.trim() !== '');
      case 2:
        return true; // All fields in step 2 have defaults
      case 3:
        return config.createNewIndex ? config.newIndexName.trim() !== '' : config.targetIndex !== '';
      case 4:
        // Scheduling validation
        if (config.schedule.enabled) {
          if (!config.schedule.timezone) return false;
          if (config.schedule.frequency === 'custom' && !config.schedule.cronExpression) return false;
          if (config.schedule.frequency === 'weekly' && config.schedule.dayOfWeek === undefined) return false;
          if (config.schedule.frequency === 'monthly' && !config.schedule.dayOfMonth) return false;
          if (config.schedule.frequency === 'hourly' && !config.schedule.intervalMinutes) return false;
        }
        return true;
      default:
        return false;
    }
  };

  return (
    <Panel
      isOpen={isVisible}
      onDismiss={onDismiss}
      type={PanelType.large}
      headerText="Setup Public Web Connector"
      closeButtonAriaLabel="Close"
      isFooterAtBottom={true}
      onRenderFooterContent={() => (
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <DefaultButton
            text="Cancel"
            onClick={onDismiss}
            disabled={loading}
          />
          {step > 1 && (
            <DefaultButton
              text="Previous"
              onClick={() => setStep(step - 1)}
              disabled={loading}
            />
          )}
          {step < totalSteps ? (
            <PrimaryButton
              text="Next"
              onClick={() => setStep(step + 1)}
              disabled={!canProceedToNextStep() || loading}
            />
          ) : (
            <PrimaryButton
              text={loading ? "Creating..." : "Create Connector"}
              onClick={handleSubmit}
              disabled={!canProceedToNextStep() || loading}
            />
          )}
        </Stack>
      )}
    >
      <Stack tokens={{ childrenGap: 20 }}>
        {/* Header with icon */}
        <Stack horizontal tokens={{ childrenGap: 15 }} verticalAlign="center">
          <GlobeRegular style={{ fontSize: 32, color: '#4caf50' }} />
          <Stack>
            <Text variant="xLarge" style={{ fontWeight: 600 }}>
              Public Web Connector
            </Text>
            <Text variant="medium" style={{ color: '#605e5c' }}>
              Crawl and index content from public websites
            </Text>
          </Stack>
        </Stack>

        {/* Progress indicator */}
        <Stack tokens={{ childrenGap: 10 }}>
          <Stack horizontal horizontalAlign="space-between">
            <Text variant="medium" style={{ fontWeight: 600 }}>
              Step {step} of {totalSteps}
            </Text>
            <Text variant="small" style={{ color: '#605e5c' }}>
              {step === 1 && 'Basic Configuration'}
              {step === 2 && 'Crawling Settings'}
              {step === 3 && 'Processing & Index'}
            </Text>
          </Stack>
          <ProgressIndicator percentComplete={getStepProgress()} />
        </Stack>

        {/* Messages */}
        {message && (
          <MessageBar messageBarType={message.type} onDismiss={() => setMessage(null)}>
            {message.text}
          </MessageBar>
        )}

        {/* Loading indicator */}
        {loading && (
          <Stack horizontalAlign="center" tokens={{ childrenGap: 10 }}>
            <Spinner size={SpinnerSize.large} />
            <Text>Creating public web connector job...</Text>
          </Stack>
        )}

        {/* Step content */}
        {!loading && getStepContent()}

        {/* URL validation section */}
        {step === 1 && config.startUrls.some(url => url.trim() !== '') && (
          <Stack tokens={{ childrenGap: 15 }}>
            <Separator />
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="medium" style={{ fontWeight: 600 }}>
                URL Validation
              </Text>
              <DefaultButton
                text={validating ? "Validating..." : "Validate URLs"}
                onClick={validateUrls}
                disabled={validating}
                iconProps={{ iconName: validating ? undefined : 'CheckMark' }}
              />
            </Stack>
            
            {Object.keys(validationResults).length > 0 && (
              <Stack tokens={{ childrenGap: 10 }}>
                {Object.entries(validationResults).map(([url, result]) => (
                  <Stack key={url} horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                    {result.valid ? (
                      <CheckmarkCircleRegular style={{ color: '#107c10', fontSize: 16 }} />
                    ) : (
                      <WarningRegular style={{ color: '#d13438', fontSize: 16 }} />
                    )}
                    <Stack style={{ flex: 1 }}>
                      <Text variant="small" style={{ fontWeight: 600 }}>{url}</Text>
                      <Text variant="small" style={{ color: result.valid ? '#107c10' : '#d13438' }}>
                        {result.message}
                      </Text>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        )}

        {/* Discovery Preview Section */}
        {step >= 2 && config.startUrls.some(url => url.trim() !== '') && config.contentTypes.length > 0 && (
          <Stack tokens={{ childrenGap: 15 }}>
            <Separator />
            <Stack tokens={{ childrenGap: 10 }} style={{ 
              padding: '12px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '6px',
              border: '1px solid #e1e1e1'
            }}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack tokens={{ childrenGap: 2 }}>
                  <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                    <SearchRegular style={{ fontSize: 16, color: '#0078d4' }} />
                    <Text variant="medium" style={{ fontWeight: 600 }}>Discovery Preview</Text>
                  </Stack>
                  <Text variant="small" style={{ color: '#666' }}>
                    Preview what content will be crawled using your configured filters
                  </Text>
                </Stack>
                <PrimaryButton
                  text={discoveryLoading ? "Discovering..." : "🔍 Run Discovery"}
                  iconProps={{ iconName: discoveryLoading ? undefined : 'Search' }}
                  onClick={handleDiscovery}
                  disabled={discoveryLoading || config.startUrls.filter(url => url.trim().length > 0).length === 0 || config.contentTypes.length === 0}
                />
              </Stack>
              
              {discoveryLoading && (
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" style={{
                  padding: '8px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e1e1e1'
                }}>
                  <Spinner size={SpinnerSize.small} />
                  <Stack tokens={{ childrenGap: 2 }}>
                    <Text variant="small" style={{ fontWeight: 500 }}>
                      Analyzing website structure...
                    </Text>
                    <Text variant="small" style={{ color: '#666', fontSize: '10px' }}>
                      This may take 10-30 seconds
                    </Text>
                  </Stack>
                </Stack>
              )}

              {!discoveryLoading && (
                <Stack tokens={{ childrenGap: 4 }} style={{
                  padding: '8px',
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e1e1e1'
                }}>
                  <Text variant="small" style={{ fontWeight: 500, color: '#323130' }}>
                    💡 Discovery validates site access and previews content
                  </Text>
                  <Stack tokens={{ childrenGap: 2 }}>
                    <Text variant="small" style={{ color: '#666', fontSize: '11px' }}>
                      • Verify website access • Count discoverable content • Preview file types
                    </Text>
                  </Stack>
                  
                  {config.startUrls.filter(url => url.trim().length > 0).length === 0 && (
                    <Text variant="small" style={{ color: '#d83b01', fontStyle: 'italic', fontSize: '10px' }}>
                      ⚠️ Please enter a start URL above
                    </Text>
                  )}
                </Stack>
              )}

              {/* Discovery Results */}
              {discoveryResult && (
                <Stack tokens={{ childrenGap: 10 }} style={{
                  padding: '10px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '6px',
                  border: '1px solid #0078d4'
                }}>
                  <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                    <CheckmarkCircleRegular style={{ fontSize: 16, color: '#107c10' }} />
                    <Text variant="small" style={{ fontWeight: 600, color: '#107c10' }}>
                      Discovery Complete
                    </Text>
                  </Stack>
                  
                  {/* Stats Overview */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '8px',
                    backgroundColor: '#fff',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #e1e1e1'
                  }}>
                    <div style={{ textAlign: 'center', padding: '8px' }}>
                      <Text variant="large" style={{ fontWeight: 600, color: '#0078d4', display: 'block' }}>
                        {discoveryResult.totalDocuments.toLocaleString()}
                      </Text>
                      <Text variant="small" style={{ color: '#666' }}>Documents</Text>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px' }}>
                      <Text variant="large" style={{ fontWeight: 600, color: '#107c10', display: 'block' }}>
                        {Object.keys(discoveryResult.fileTypeBreakdown).length}
                      </Text>
                      <Text variant="small" style={{ color: '#666' }}>File Types</Text>
                    </div>
                    <div style={{ textAlign: 'center', padding: '8px' }}>
                      <Text variant="large" style={{ fontWeight: 600, color: '#d83b01', display: 'block' }}>
                        ~{Math.round(discoveryResult.estimatedProcessingTime / 60)}m
                      </Text>
                      <Text variant="small" style={{ color: '#666' }}>Est. Time</Text>
                    </div>
                  </div>

                  {/* File Types Found */}
                  {Object.keys(discoveryResult.fileTypeBreakdown).length > 0 && (
                    <Stack tokens={{ childrenGap: 6 }}>
                      <Text variant="small" style={{ fontWeight: 600 }}>📄 File Types Found:</Text>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {Object.entries(discoveryResult.fileTypeBreakdown).map(([fileType, count]) => (
                          <div key={fileType} style={{
                            padding: '2px 8px',
                            backgroundColor: '#fff4e6',
                            borderRadius: '12px',
                            fontSize: '10px'
                          }}>
                            {fileType} ({count})
                          </div>
                        ))}
                      </div>
                    </Stack>
                  )}

                  {/* Sample URLs */}
                  {discoveryResult.discoveredUrls.length > 0 && (
                    <Stack tokens={{ childrenGap: 6 }}>
                      <Text variant="small" style={{ fontWeight: 600 }}>🔗 Discovered URLs:</Text>
                      {discoveryResult.discoveredUrls.slice(0, 10).map((url, index) => (
                        <div key={index} style={{
                          padding: '6px 8px',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          border: '1px solid #e1e1e1'
                        }}>
                          <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
                            <DocumentRegular style={{ fontSize: 14, color: '#0078d4' }} />
                            <Stack tokens={{ childrenGap: 1 }} style={{ flex: 1 }}>
                              <Text variant="small" style={{ fontWeight: 500 }}>{url.title}</Text>
                              <Text variant="small" style={{ color: '#666', fontSize: '10px' }}>
                                {url.file_type} • {(url.size_estimate / 1024).toFixed(1)}KB
                              </Text>
                            </Stack>
                          </Stack>
                        </div>
                      ))}
                      {discoveryResult.discoveredUrls.length > 10 && (
                        <Text variant="small" style={{ color: '#666', fontStyle: 'italic' }}>
                          ... and {discoveryResult.discoveredUrls.length - 10} more documents
                        </Text>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Panel>
  );
};

export default PublicWebConnectorSetup;
